import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {LoginResultRecord, PoliciesFpnetDbRecordsPolicy, ProfiloAccessoAndPolicies} from '@commons/mapping/LoginResultRecord';
import {cambioPassword, login, resetAccount, resetPasswordAccount} from './actions/authentication.action';
import {contrattoNullo, CoreState, initialState, RifContratto} from './core.state';
import {RootState} from '@app/store';
import {AziendaMin} from '@commons/mapping/AziendaNavRecord';
import {AziendaRecord} from '@commons/mapping/AziendaRecord';
import {LavoratoreRecord} from "@commons/mapping/LavoratoreRecord";
import {SitePreferences, TokenRecord} from '@commons/mapping/TokenRecord';
import {LavoratoriFilter} from '@commons/filters';
import {UserCRUDRecord} from '@commons/mapping/UserCRUDRecord';
import {DateTime} from 'luxon';
import {updateUser} from '@commons/actions/user.action';
import {IndiceMese} from "@commons/types";
import {fetchConfig} from "@commons/utils/fetchBaseFPNetQuery";

interface LoginOptions {
    rememberMe?: boolean,
}

interface LoginSuccessPayload {
    loginResult: LoginResultRecord;
    loginOptions?: LoginOptions;
}

const setBusy = (state: { isBusy: boolean }) => {
    state.isBusy = true;
};
const resetBusy = (state: { isBusy: boolean }) => {
    state.isBusy = false;
};

const _setContratto = (state: CoreState, payload: LavoratoreRecord) => {
    //console.log('_setContratto');
    const idAzienda = state.loginResult.token.idAzienda;
    state.contratto = {...payload, idAzienda};
    // + reset lastContratto in modo da nn usarlo più!
    state.nextContratto = null;

    if (state.persistData)
        state.persistData = {
            ...state.persistData, contratto: {
                idAziendaMaster: payload.idAziendaMaster,
                idAzienda,
                id: payload.id,
            },
        };
    else {
        throw new Error('persistData is null!');
    }
};

const _resetContratto = (state: CoreState) => {
    state.contratto = null;
    if (fetchConfig.platformType === 'web') { // Solo per web, resetto contratto anche dopo logout
        state.persistData.contratto = contrattoNullo;
    }
};

const setupNextContratto = (state: CoreState, loginResult: LoginResultRecord) => {
    if (loginResult.token.idAziendaMaster === state.persistData.contratto.idAziendaMaster &&
        loginResult.token.idAzienda === state.persistData.contratto.idAzienda &&
        state.persistData.contratto.id) {

        state.nextContratto = {...state.persistData.contratto};

        // hot fix per problema con app e presenza di più contratti per il singolo lavoratore
        // evito di resettare se il contratto corrente è pari a quello precedente (action.payload.idDip ==> idDip da persistData)
        /*if (state.contratto && state.contratto.id !== action.payload.idDip)
        {
            _resetContratto(state);
        }*/
    } else {
        state.nextContratto = null;
    }
    _resetContratto(state);
}

export const coreSlice = createSlice({
        name: 'authSlice',
        initialState,
        // The `reducers` field lets us define reducers and generate associated actions
        reducers: {
            anonymousAccessSuccess: (state) => {
                // Redux Toolkit allows us to write "mutating" logic in reducers. It
                // doesn't actually mutate the state because it uses the Immer library,
                // which detects changes to a "draft state" and produces a brand new
                // immutable state based off those changes
                state.loginResult = {} as LoginResultRecord;
                state.status = 'RUN';
            },
            systemReady: (state) => {
                // Redux Toolkit allows us to write "mutating" logic in reducers. It
                // doesn't actually mutate the state because it uses the Immer library,
                // which detects changes to a "draft state" and produces a brand new
                // immutable state based off those changes
                state.loginResult = {} as LoginResultRecord;
                state.status = 'READY';
                state.persistData = {...state.persistData, token: ''};
            },
            systemWarning: (state) => {
                state.status = 'ISEXPIRING';
            },
            systemExpired: (state) => {
                state.status = 'EXPIRED';
            },
            loginSuccess: (state, action: PayloadAction<LoginSuccessPayload>) => {
                // Redux Toolkit allows us to write "mutating" logic in reducers. It
                // doesn't actually mutate the state because it uses the Immer library,
                // which detects changes to a "draft state" and produces a brand new
                // immutable state based off those changes
                state.loginResult = action.payload.loginResult;
                state.status = 'RUN';
                if (action.payload.loginOptions && action.payload.loginOptions.rememberMe) {
                    state.persistData = {
                        ...state.persistData,
                        token: action.payload.loginResult.token.token,
                        userName: action.payload.loginResult.user.userName,
                    };
                }
                setupNextContratto(state, action.payload.loginResult);
            },
            waitingForActivation: (state, action: PayloadAction<LoginSuccessPayload | null>) => {
                // Redux Toolkit allows us to write "mutating" logic in reducers. It
                // doesn't actually mutate the state because it uses the Immer library,
                // which detects changes to a "draft state" and produces a brand new
                // immutable state based off those changes
                if (action.payload) {
                    state.loginResult = action.payload.loginResult;
                    if (action.payload.loginOptions && action.payload.loginOptions.rememberMe) {
                        state.persistData = {
                            ...state.persistData,
                            token: action.payload.loginResult.token.token,
                            userName: action.payload.loginResult.user.userName,
                        };
                    }
                }
                state.status = 'WAITINGFORACTIVATION';
                _resetContratto(state);
            },
            refreshSuccess: (state, action: PayloadAction<string>) => {
                state.loginResult.token.tokenExpires = action.payload;
                // Modifica del 08/08/2023 (Sergio), refreshSuccess NON DEVE cambiare lo stato applicativo!
                //state.status = 'RUN';
                // Modifica del 04/01/2024 (Sergio), refreshSuccess DEVE cambiare lo stato applicativo!
                // La precedente modifica del 8/8/23 era stata fatta per l'app mobile, ora su WEB se non ripristino lo stato rimarrò in ISEXPIRING per sempre!
                // Quindi rispetto alla formula iniziale dove per qualsiasi refreshSuccess lo stato tornava RUN, ora lo faccio solo le arrivo da ISEXPIRING
                if (state.status === 'ISEXPIRING')
                    state.status = 'RUN';
            },
            //validateSuccess: (state, action: PayloadAction<{ loginResult: LoginResultRecord, idAziendaMaster: number, idAzienda: number, idDip: number }>) => {
            validateSuccess: (state, action: PayloadAction<{ loginResult: LoginResultRecord }>) => {
                // Redux Toolkit allows us to write "mutating" logic in reducers. It
                // doesn't actually mutate the state because it uses the Immer library,
                // which detects changes to a "draft state" and produces a brand new
                // immutable state based off those changes
                state.loginResult = action.payload.loginResult;
                state.status = 'RUN';
                setupNextContratto(state, action.payload.loginResult);
            },
            loginFailed: (state) => {
                // Redux Toolkit allows us to write "mutating" logic in reducers. It
                // doesn't actually mutate the state because it uses the Immer library,
                // which detects changes to a "draft state" and produces a brand new
                // immutable state based off those changes
                state.loginResult = {} as LoginResultRecord;
                state.status = 'READY';
            },
            logoutSuccess: (state) => {
                // Redux Toolkit allows us to write "mutating" logic in reducers. It
                // doesn't actually mutate the state because it uses the Immer library,
                // which detects changes to a "draft state" and produces a brand new
                // immutable state based off those changes
                state.loginResult = {} as LoginResultRecord;
                state.azienda = null;
                state.aziendaMaster = null;
                state.status = 'READY';
                state.persistData.token = '';
                _resetContratto(state);
            },
            passwordExpired: (state, action: PayloadAction<LoginResultRecord>) => {
                state.loginResult = action.payload;
                state.status = 'PWD_EXPIRED';
            },
            firstLoginSuccess: (state, action: PayloadAction<LoginResultRecord>) => {
                state.loginResult = action.payload;
                state.status = 'FIRST_LOGIN';
            },
            changeAzienda: (state, action: PayloadAction<AziendaMin>) => {
                state.loginResult.token.idAziendaMaster = action.payload.idAziendaMaster;
                state.loginResult.token.idAzienda = action.payload.idAzienda;
                if (action.payload.idDominio)
                    state.loginResult.token.idDominio = action.payload.idDominio;
                if (action.payload.URI || action.payload.URI === null) {
                    if (action.payload.URI !== null && state.aziendaNavLink !== action.payload.URI)
                        console.log('ATTENZIONE!!!');
                    state.aziendaNavLink = action.payload.URI;
                }
            },
            searchAzienda: (state) => {
            },
            changePeriodoSuccess: (state, action: PayloadAction<{ anno: number, mese: number }>) => {
                state.loginResult.token.anno = action.payload.anno;
                state.loginResult.token.mese = action.payload.mese;
            },
            changeTokenSuccess: (state, action: PayloadAction<TokenRecord>) => {
                if (state.loginResult.token.idAzienda !== action.payload.idAzienda) {
                    _resetContratto(state);
                    state.nextContratto = null;
                }

                state.loginResult.token = action.payload;
            },
            changeAziendaNavLink: (state, action: PayloadAction<{ link: string | null, setIsBusy?: boolean }>) => {
                if (action.payload.setIsBusy) {
                    state.isBusy = true;
                }
                state.aziendaNavLink = action.payload.link;
            },
            setupContratto: (state, action: PayloadAction<LavoratoreRecord>) => {
                _setContratto(state, action.payload);
            },
            resetContratto: _resetContratto,
            setupAzienda: (state, action: PayloadAction<AziendaRecord | null>) => {
                state.azienda = action.payload;
            },
            setupAziendaMaster: (state, action: PayloadAction<AziendaRecord | null>) => {
                state.aziendaMaster = action.payload;
            },
            updateUserSuccess: (state, action: PayloadAction<Partial<UserCRUDRecord>>) => {
                const updatedUser = action.payload;
                if (updatedUser.email || updatedUser.email === '')
                    state.loginResult.user.email = updatedUser.email;
                if (updatedUser.emailNotificheWF || updatedUser.emailNotificheWF === '')
                    state.loginResult.user.emailNotificheWF = updatedUser.emailNotificheWF;
                if (updatedUser.telefono || updatedUser.telefono === '')
                    state.loginResult.user.telefono = updatedUser.telefono;
                if (updatedUser.cellulare || updatedUser.cellulare === '')
                    state.loginResult.user.cellulare = updatedUser.cellulare;
            },
            setupBusy: (state, action: PayloadAction<boolean>) => {
                state.isBusy = action.payload;
            },
            setupShowModalVideo: (state, action: PayloadAction<boolean>) => {
                state.persistData.showModalVideo = action.payload;
            }
        },
// The `extraReducers` field lets the slice handle actions defined elsewhere,
// including actions generated by createAsyncThunk or in other slices.
        extraReducers: (builder) => {
            builder
                // LOGIN
                .addCase(login.pending, setBusy)
                .addCase(login.fulfilled, resetBusy)
                .addCase(login.rejected, resetBusy)

                // RESET PASSWORD
                .addCase(resetPasswordAccount.pending, setBusy)
                .addCase(resetPasswordAccount.fulfilled, resetBusy)
                .addCase(resetPasswordAccount.rejected, resetBusy)

                // CAMBIO PASSWORD
                .addCase(cambioPassword.pending, setBusy)
                .addCase(cambioPassword.fulfilled, resetBusy)
                .addCase(cambioPassword.rejected, resetBusy)

                // RESET ACCOUNT
                .addCase(resetAccount.pending, setBusy)
                .addCase(resetAccount.fulfilled, resetBusy)
                .addCase(resetAccount.rejected, resetBusy)

                // USER ACTIONS
                .addCase(updateUser.pending, setBusy)
                .addCase(updateUser.fulfilled, resetBusy)
                .addCase(updateUser.rejected, resetBusy);
        },
    })
;

export const {
    loginSuccess,
    refreshSuccess,
    validateSuccess,
    logoutSuccess,
    anonymousAccessSuccess,
    systemReady,
    changeAzienda,
    searchAzienda,
    changeTokenSuccess,
    changeAziendaNavLink,
    setupAzienda,
    setupAziendaMaster,
    setupContratto,
    resetContratto,
    loginFailed,
    passwordExpired,
    firstLoginSuccess,
    waitingForActivation,
    systemWarning,
    systemExpired,
    updateUserSuccess,
    setupBusy,
    setupShowModalVideo,
} = coreSlice.actions;
export default coreSlice.reducer;

// Selector:
export const selectStatus = (state: RootState) => state.auth.status;
export const selectTokenID = (state: RootState) => {
    if (state.auth.loginResult && state.auth.loginResult.token)
        return state.auth.loginResult.token.token;
    else
        return null;
};
export const selectAziendaNavLink = (state: RootState) => {
    return state.auth.aziendaNavLink;
};
export const selectDomini = (state: RootState) => {
    if (state.auth.loginResult && state.auth.loginResult.token)
        return state.auth.loginResult.domini;
    else
        return [];
};
export const selectUser = (state: RootState): UserCRUDRecord | null => {
    if (state.auth.loginResult && state.auth.loginResult.token)
        return state.auth.loginResult.user;
    else
        return null;
};

export const selectToken = createSelector([(state: RootState) => state.auth.loginResult], loginResult => {
    if (loginResult && loginResult.token)
        return loginResult.token;
    else
        return {} as TokenRecord;
});

export const selectToken0 = (state: RootState) => {
    if (state.auth.loginResult && state.auth.loginResult.token)
        return state.auth.loginResult.token;
    else
        return {} as TokenRecord;
};
export const selectIdAziendaMaster = (state: RootState) => {
    if (state.auth.loginResult && state.auth.loginResult.token)
        return state.auth.loginResult.token.idAziendaMaster;
    else
        return null;
};
export const selectIdAzienda = (state: RootState) => {
    if (state.auth.loginResult && state.auth.loginResult.token)
        return state.auth.loginResult.token.idAzienda;
    else
        return null;
};
export const selectProfiloAccessoAndPolicies = (state: RootState): ProfiloAccessoAndPolicies | null => {
    if (state.auth.loginResult && state.auth.loginResult.profilo)
        return state.auth.loginResult.profilo;
    else
        return null;
};

class CrudPolicies {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;

    constructor() {
        this.create = false;
        this.read = false;
        this.update = false;
        this.delete = false;
    }
}

export const selectScreenPolicies = createSelector(
    [(state: RootState) => state.auth.loginResult],
    loginResult => {
        const policies = {
            docs: new CrudPolicies(),
            workflow: new CrudPolicies(),
            monitorWF: new CrudPolicies(),
            terminaleVirtuale: new CrudPolicies(),
        }
        if (loginResult && loginResult.profilo && loginResult.profilo.policies) {
            const profilo = loginResult.profilo
            let policy: PoliciesFpnetDbRecordsPolicy | undefined;
            policy = profilo.policies.find(p => p.uri === '/FPNet2020/lavoratore/{idDip:\\d+}/documento/');
            if (policy) {
                policies.docs.create = policy.gPost;
                policies.docs.read = policy.gGet;
                policies.docs.update = policy.gPat || policy.gPut;
                policies.docs.delete = policy.gDel;
            }
            policy = profilo.policies.find(p => p.uri === '/FPNet2020/prePLUS/presenze/{idDip}/causaleWF/');
            if (policy) {
                policies.workflow.create = policy.gPost;
                policies.workflow.read = policy.gGet;
                policies.workflow.update = policy.gPat || policy.gPut;
                policies.workflow.delete = policy.gDel;
            }
            policy = profilo.policies.find(p => p.uri === '/FPNet2020/tool/{idAziendaMaster:\\d+}-{idAzienda:\\d+}/workflow/');
            if (policy) {
                policies.monitorWF.create = policy.gPost;
                policies.monitorWF.read = policy.gGet;
                policies.monitorWF.update = policy.gPat || policy.gPut;
                policies.monitorWF.delete = policy.gDel;
            }
            policy = profilo.policies.find(p => p.uri === '/FPNet2020/prePLUS/timbratura-lavoratore/{idDip:\\d+}/');
            if (policy) {
                policies.terminaleVirtuale.read = policy.gGet;
            }
        }
        return policies;
    }
)

export const selectScreenPolicies0 = (state: RootState): { docs: CrudPolicies, workflow: CrudPolicies, monitorWF: CrudPolicies } => {

    const policies = {
        docs: new CrudPolicies(),
        workflow: new CrudPolicies(),
        monitorWF: new CrudPolicies(),
    }
    if (state.auth.loginResult && state.auth.loginResult.profilo && state.auth.loginResult.profilo.policies) {
        const profilo = state.auth.loginResult.profilo
        let policy: PoliciesFpnetDbRecordsPolicy | undefined;
        policy = profilo.policies.find(p => p.uri === '/FPNet2020/lavoratore/{idDip:\\d+}/documento/');
        if (policy) {
            policies.docs.create = policy.gPost;
            policies.docs.read = policy.gGet;
            policies.docs.update = policy.gPat || policy.gPut;
            policies.docs.delete = policy.gDel;
        }
        policy = profilo.policies.find(p => p.uri === '/FPNet2020/prePLUS/presenze/{idDip}/causaleWF/');
        if (policy) {
            policies.workflow.create = policy.gPost;
            policies.workflow.read = policy.gGet;
            policies.workflow.update = policy.gPat || policy.gPut;
            policies.workflow.delete = policy.gDel;
        }
        policy = profilo.policies.find(p => p.uri === '/FPNet2020/tool/{idAziendaMaster:\\d+}-{idAzienda:\\d+}/workflow/');
        if (policy) {
            policies.monitorWF.create = policy.gPost;
            policies.monitorWF.read = policy.gGet;
            policies.monitorWF.update = policy.gPat || policy.gPut;
            policies.monitorWF.delete = policy.gDel;
        }
    }
    return policies;
}

export const selectPeriodo = createSelector(
    [(state: RootState) => state.auth.loginResult],
    loginResult => {
        if (loginResult && loginResult.token)
            return {
                anno: loginResult.token.anno,
                mese: loginResult.token.mese as IndiceMese,
            };
        else {
            const {year: anno, month: mese} = DateTime.now();
            return {
                anno,
                mese, // with 1 being January and 12 being December
            };
        }
    }
)

export const selectPeriodo1 = (state: RootState): { anno: number, mese: IndiceMese } => {
    if (state.auth.loginResult && state.auth.loginResult.token)
        return {
            anno: state.auth.loginResult.token.anno,
            mese: state.auth.loginResult.token.mese as IndiceMese,
        };
    else {
        const now = DateTime.now();
        return {
            anno: now.year,
            mese: now.month as IndiceMese, // with 1 being January and 12 being December
        };
    }
};
export const selectLavoratoriFilter = (state: RootState) => {
    const def = {} as LavoratoriFilter;
    if (state.auth.loginResult && state.auth.loginResult.token)
        return state.auth.loginResult.token.sitePreferences ? state.auth.loginResult.token.sitePreferences.lavoratoriFilter : def;
    return def;
};
export const selectSitePreferences = (state: RootState) => {
    const def = {} as SitePreferences;
    if (state.auth.loginResult && state.auth.loginResult.token)
        return state.auth.loginResult.token.sitePreferences || {lavoratoriFilter: {iniziale: '*', selectAll: true}} as SitePreferences;
    return def;
};
export const selectIsBusy = (state: RootState) => state.auth.isBusy;
export const selectIsLogged = (state: RootState) => (state.auth.status === 'RUN' && selectToken(state).token);
export const selectIsAnonymous = (state: RootState) => (state.auth.status === 'RUN' && !(selectToken(state).token));
export const selectLavoratore = (state: RootState) => (state.auth.contratto);
export const selectLastLavoratore = (state: RootState) => (state.auth.nextContratto);
export const selectAzienda = (state: RootState) => (state.auth.azienda);
export const selectAziendaMaster = (state: RootState) => (state.auth.aziendaMaster);
export const selectShowModalVideo = (state: RootState) => (state.auth.persistData.showModalVideo === undefined ? true : state.auth.persistData.showModalVideo);
export const selectPersistToken = (state: RootState) => (state.auth.persistData && state.auth.persistData.token) ? state.auth.persistData.token : '';
export const selectPersistContratto = (state: RootState): RifContratto => (state.auth.persistData && state.auth.persistData.contratto ? state.auth.persistData.contratto : contrattoNullo);
